import { Action, createBrowserHistory, PartialLocation, To } from "history";
import * as React from "react";
import ReactDOM from "react-dom";
import toRegex from "path-to-regexp";
import Application from "../Application";
import CheckLoginStatusInteractor from "../authentication/interactor/CheckLoginStatusInteractor";
import LoadUserPreferencesInteractor from "../authentication/interactor/LoadUserPreferencesInteractor";
import { AppConstants, Layouts } from "../core/common/Constants";
import { ITranslationService } from "../core/service/locale/TranslationService";
import { INotificationService } from "../core/service/notification/NotificationService";
import AppLayout from "../core/ui/layouts/AppLayout";
import TLayoutMap from "../core/ui/layouts/TLayoutMap";
import LoadDefaultStateInteractor from "../core/interactor/language/LoadDefaultStateInteractor";
import LoadApplicationInteractor from "../portal/interactors/LoadApplicationInteractor";
import { ITrackingService } from "../core/service/TrackingService";

export interface TRouteOptions {
  secure?: boolean;
  roles?: string | string[];
  name?: string;
  layout?: string;
  initialProps?: any;
}
export interface TRoute {
  path: string;
  page: any;

  options?: TRouteOptions;
}

export interface IRouter {
  registerRoutes(...routes: TRoute[]): void;
  showLocation(
    locationDescription: { action: Action; location: To } | string
  ): Promise<any>;
  replace(location: PartialLocation): void;
  push(location: PartialLocation): void;
  bindLocationListener(): void;
  goBack(): void;
  reload(): void;
  location: TRoute;
}
const Router = (
  context,
  rootElement,
  application: Application,
  routes: TRoute[],
  routerConfiguration: TLayoutMap
) => {
  let _history = createBrowserHistory({});
  let _routes = routes;
  let _currentRoute: TRoute;
  let _rootElement: HTMLElement = rootElement;
  let _application = application;
  let _notificationService: INotificationService =
    _application.container.resolve<INotificationService>("notificationService");
  const _trackingService: ITrackingService =
    _application.container.resolve<ITrackingService>("trackingService");

  function registerRoutes(...routeCollection: TRoute[]) {
    _routes = [..._routes, ...routeCollection];
  }

  function matchURI(path, uri, search = "", hash = "") {
    const keys = [];
    const pattern = toRegex(path, keys);
    const match = pattern.exec(uri);
    if (!match) return null;
    let params = Object.create(null);
    for (let i = 1; i < match.length; i++) {
      params[(keys[i - 1] as any).name] =
        match[i] !== undefined ? match[i] : undefined;
    }
    if (search) {
      const urlParams = new URLSearchParams(search);
      for (var entry of urlParams.entries()) {
        params[entry[0]] = entry[1];
      }
    }
    return params;
  }

  function buildPage(route: TRoute, props) {
    let _layoutType =
      (route.options && route.options.layout) || Layouts.DEFAULT;
    let layout: any;
    switch (_layoutType) {
      case Layouts.APP:
        layout = routerConfiguration.appLayout;
        break;
      case Layouts.ROOT:
        layout = routerConfiguration.rootLayout;
        break;
      case Layouts.BASE:
        layout = routerConfiguration.baseLayout;
        break;
      case Layouts.PORTAL:
        layout = routerConfiguration.appLayout;
        break;
      default:
        layout = AppLayout;
    }
    let pageProps = {
      ...props,
      ...{
        store: _application.store,
        router: _router,
        user: _application.store.getState<any>()?.user,
        organisation: _application.store.getState<any>()?.userOrganisation,
        translate:
          _application.container.resolve<ITranslationService>(
            "translationService"
          ).get
      }
    };
    let page = React.createElement(
      layout,
      pageProps,
      React.createElement(route.page, pageProps)
    );
    return page;
  }

  async function resolve(routesList: TRoute[], routeContext: any) {
    for (const route of routesList) {
      if (routeContext.error) {
        console.error(routeContext.error);
      }
      const uri = routeContext.error ? "/error" : routeContext.pathname;
      const params = matchURI(
        route.path,
        uri,
        routeContext.search,
        routeContext.hash
      );
      if (!params) continue;
      _currentRoute = route;

      if (route.options && route.options.secure) {
        const isLoggedIn = await _application.container
          .resolve<CheckLoginStatusInteractor>("checkLoginStatus")
          .execute();
        if (!isLoggedIn) {
          return replace({
            ...routeContext,
            pathname: "/login",
            search: `?redirect=${encodeURI(
              routeContext.pathname + (routeContext.search || "")
            )}`
          });
        } else {
          await _application.container
            .resolve<LoadUserPreferencesInteractor>("loadUserPreferences")
            .execute();
        }
      }
      if (application.applicationName === AppConstants.PORTAL) {
        _trackingService.trackPageView({
          path: routeContext.pathname + routeContext.search
        });
        await _application.container
          .resolve<LoadDefaultStateInteractor>("loadDefaultState")
          .execute(params.organisationId || undefined);

        _application.container
          .resolve<LoadApplicationInteractor>("loadApplication")
          .execute();
      }
      let node = _rootElement;
      let props = Object.assign(
        {},
        route.options || {},
        (route.options && route.options.initialProps) || {},
        {
          params: Object.assign({}, params, routeContext.state || {})
        }
      );
      let page = buildPage(route, props);
      _rootElement.scrollIntoView({
        behavior: "smooth"
      });
      return ReactDOM.render(page, node);
    }
    const error = new Error(`Route ${routeContext.pathname} not found`) as any;
    error.status = 404;
    throw error;
  }

  function push(location: PartialLocation) {
    const { state, ...other } = location;
    if (other.pathname && !other.search) {
      let params = other.pathname.split("?");

      other.pathname = params[0];
      if (params.length > 1) {
        other.search = "?" + params[1];
      } else {
        other.search = undefined;
      }
    }
    return _history.push(other, state);
  }

  function goBack() {
    return _history.back();
  }

  function replace(location: PartialLocation) {
    const { state, ...other } = location;
    return _history.replace(other, state);
  }

  async function showLocation(
    locationDescription:
      | {
          action: Action;
          location: To;
        }
      | string
  ) {
    try {
      let _location: To | string;
      if (typeof locationDescription === "string") {
        window.location.search = "";
        _location = {
          pathname: locationDescription,
          search: ""
        };
      } else {
        _location = locationDescription.location;
      }
      await resolve(_routes, _location);
      return;
    } catch (error: any) {
      if (error.status) {
        switch (error.status) {
          case 401:
            _notificationService.showError(error.message);
            return replace({
              ...context,
              pathname: `/login`,
              search: `?redirect=${encodeURI(
                context.pathname + (context.search || "")
              )}`
            });
          case 404:
            return replace({
              pathname: `/error`
            });
          default:
            _notificationService.showError(error.message);
        }
      }
    }
  }

  function registerLocationListener() {
    _history.listen(showLocation);
  }

  var _router = {
    registerRoutes,
    showLocation,
    replace,
    push,
    bindLocationListener: registerLocationListener,
    goBack,
    reload() {
      _history.go(0);
    },
    get location() {
      return _currentRoute;
    }
  } as IRouter;

  return _router;
};

export { Router };
